import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useParams, Link, useNavigate } from "react-router-dom";
import "./productDetails.css";
import { useBrand } from "../../context/brand-context";
import ProductWishlistButton from "./ProductWishlistButton";
import { FiShoppingCart, FiPlus, FiMinus } from "react-icons/fi";
import http from "../../api/http";
import { useAuth } from "../../context/AuthContext";
import { useDispatch, useSelector } from "react-redux";
import {
  addToCart,
  setQty as setCartQty,
  increaseQty as incCartQty,
  decreaseQty as decCartQty,
  removeFromCart,
  selectCartItems,
} from "../../context/MyCartSlice";

function fmt(x) {
  const n = Number(x);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

function mapFromItem(item) {
  if (!item) return null;

  const lib = item?.LibArt || "Article";
  const cod = item?.CodArt || "";
  const refArt = item?.RefArt || "";
  const codArtOrig = item?.CodArtOrig || "";
  const ref = refArt || item?.RepXpert?.ArtNumber || item?.TecDoc?.ArtNumber || "";
  const brandLabel = item?.Marque || item?.RepXpert?.Brand || "";
  const priceTTCraw = item?.PrixTTC ?? item?.Prixvente ?? item?.PrixVente ?? null;
  const priceTTC = priceTTCraw == null ? null : Number(priceTTCraw);
  const tva = item?.TVA != null ? Number(item.TVA) : null;
  const remise = item?.Remise != null ? Number(item.Remise) : null;
  const qt = Number(item?.Qt || 0);
  const inStock = qt > 0;
  const origine = item?.origine || "";

  // Image article = TabStock.imgArt servie par l'API (ImgArt) — pas d'image RepXpert/TecDoc
  const images = item?.ImgArt ? [item.ImgArt] : [];
  const image = images[0] || null;

  const info = item?.TecDoc?.InfoMain;
  const firstArticle = info?.articles?.[0];

  // Specs: RepXpert.Specs is an object {label: value}, else TecDoc array
  const specsRaw = item?.RepXpert?.Specs;
  const specs = specsRaw && typeof specsRaw === "object" && !Array.isArray(specsRaw)
    ? Object.entries(specsRaw).filter(([k]) => k).map(([k, v]) => ({ name: k, value: String(v || "—") }))
    : Array.isArray(specsRaw) && specsRaw.length
      ? specsRaw.map(s => ({ name: s?.name || s?.criteriaName || "", value: String(s?.value || s?.criteriaValue || "—") }))
      : (firstArticle?.allSpecifications || [])
          .filter(s => s?.criteriaName)
          .map(s => ({ name: s.criteriaName, value: s.criteriaValue || "—" }));

  const cars = (firstArticle?.compatibleCars || []).map(c => ({
    make: c?.manufacturerName || "",
    model: c?.modelName || "",
    engine: c?.typeEngineName || "",
    start: c?.constructionIntervalStart || "",
    end: c?.constructionIntervalEnd || "",
  }));

  const supplier = firstArticle?.supplierName || item?.RepXpert?.Brand || "";

  const repXpert = item?.RepXpert?.enrichi
    ? {
        brand: item.RepXpert.Brand || "",
        artNumber: item.RepXpert.ArtNumber || "",
        designation: item.RepXpert.Designation || "",
        ean: item.RepXpert.EAN || "",
        statut: item.RepXpert.Statut || "",
        images,
        oem: item.RepXpert.OEM || [],
        oemDetail: item.RepXpert.OEMDetail || [],
        vehicules: item.RepXpert.Vehicules || [],
        produits: item.RepXpert.Produits || [],
      }
    : null;

  return { lib, cod, ref, refArt, codArtOrig, brandLabel, priceTTC, tva, remise, qt, inStock, origine, image, images, supplier, specs, cars, repXpert };
}

// Group OEMDetail by manufacturer
function groupByManufacturer(oemDetail) {
  const map = {};
  for (const entry of oemDetail) {
    const mfr = entry.manufacturer || "Autre";
    if (!map[mfr]) map[mfr] = [];
    map[mfr].push(entry);
  }
  return Object.entries(map);
}

export default function ProductDetailsPage() {
  const { brand } = useBrand();
  const isAmen = brand === "amen";
  const brandColor = isAmen ? "#7a0f18" : "#2563eb";
  const priceColor = isAmen ? "#111827" : brandColor;

  const { state } = useLocation();
  const params = useParams();

  const [sourceItem, setSourceItem] = useState(() => state?.item || null);
  const dbName = sourceItem?.origine || (isAmen ? "Amen" : "Amen_C");
  const [loading, setLoading] = useState(!state?.item);
  const [imgIdx, setImgIdx] = useState(0);
  const navigate = useNavigate();

  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const cartItems = useSelector(selectCartItems);

  const cartKey = useMemo(() => {
    const it = sourceItem || {};
    return it?.CodArt ?? it?.RefArt ?? it?.TecDoc?.ArtNumber ?? "NA";
  }, [sourceItem]);

  const cartLine = cartItems.find((x) => String(x.CodArt) === String(cartKey));
  const qtyInCart = Number(cartLine?.qty || 0);

  const [qty, setQty] = useState(1);
  useEffect(() => { if (qtyInCart > 0) setQty(qtyInCart); }, [qtyInCart]);
  useEffect(() => { setImgIdx(0); }, [sourceItem]);

  const clamp = (n) => Math.max(0, parseInt(n || 0, 10));

  const scrollToProducts = () => {
    if (location.pathname === "/espace") {
      const el = document.getElementById("products-section");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    navigate({ pathname: "/espace", hash: "#products-section" });
  };

  const m = useMemo(() => mapFromItem(sourceItem), [sourceItem]);

  const onAddToCart = () => {
    if (!sourceItem || !m?.inStock) return;
    dispatch(addToCart({ item: sourceItem, qty: clamp(qty) || 1, dbName: isAmen ? "Amen" : "Amen_C" }));
  };

  const onDec = () => {
    if (!cartKey) return;
    if (qtyInCart <= 1) { dispatch(removeFromCart(cartKey)); setQty(1); }
    else dispatch(decCartQty(cartKey));
  };
  const onInc = () => { if (cartKey) dispatch(incCartQty(cartKey)); };

  const onChangeQtyInput = (e) => {
    const v = clamp(e.target.value);
    if (!cartKey) return;
    if (v < 1) { dispatch(removeFromCart(cartKey)); setQty(1); }
    else { setQty(v); dispatch(setCartQty({ CodArt: cartKey, qty: v })); }
  };

  const showAddButton = !m?.inStock ? true : qtyInCart < 1;

  useEffect(() => {
    let alive = true;
    const fetchByCodArt = async (codArt) => {
      // Route backend = POST /api/articles (codArt en query → mode fiche complète)
      const { data } = await http.post(
        `/api/articles`,
        { codtiers: user?.codtiers || null },
        { headers: { "x-db": dbName }, params: { codArt } }
      );
      return data?.item || data?.items?.[0] || (Array.isArray(data) ? data[0] : null);
    };

    // L'item venant de la grille est la version "liste" (sans Specs/OEMDetail/Vehicules) :
    // on l'affiche tout de suite, puis on charge TOUJOURS la fiche complète et on fusionne.
    const fromState = state?.item || null;
    if (fromState) setSourceItem(fromState);

    (async () => {
      try {
        setLoading(!fromState);
        const codArt = fromState?.CodArt || params.id;
        if (!codArt) return;
        const fullItem = await fetchByCodArt(codArt);
        if (!alive || !fullItem) return;
        setSourceItem((prev) => {
          if (!prev) return fullItem;
          const rp = fullItem.RepXpert?.enrichi ? fullItem.RepXpert : (prev.RepXpert || fullItem.RepXpert);
          return { ...prev, ...fullItem, RepXpert: rp };
        });
      } catch (e) {
        console.error("ProductDetails fetch error:", e?.response?.data || e?.message);
        if (alive && !fromState) setSourceItem(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [state?.item, params.id, dbName, user?.codtiers]);

  return (
    <main className="prod-page">
      <div className="prod-container">
        <header className="prod-page__head">
          <div className="prod-crumbs">
            <Link to="/espace#products-section" onClick={(e) => { e.preventDefault(); scrollToProducts(); }}>
              Produits
            </Link>
            <span>{">"}</span>
            <span>Détails</span>
          </div>
        </header>

        {loading && <section className="prod-card skel-card" aria-busy="true">Chargement…</section>}

        {!loading && !m && (
          <section className="prod-card">
            <div className="muted">Produit introuvable.</div>
          </section>
        )}

        {!loading && m && (
          <>
            {/* ── Section principale : galerie + infos ── */}
            <section className="prod-card prod-card--page">
              <div className="prod-headGrid">

                {/* Galerie d'images */}
                <div className="prod-gallery">
                  <div className="prod-imgWrap">
                    {m.images?.length
                      ? <img src={m.images[imgIdx] || m.images[0]} alt={m.lib} />
                      : <div className="prod-imgPh" />
                    }
                  </div>
                  {m.images?.length > 1 && (
                    <div className="prod-thumbs">
                      {m.images.map((src, i) => (
                        <button
                          key={i}
                          className={`prod-thumb${i === imgIdx ? " active" : ""}`}
                          onClick={() => setImgIdx(i)}
                          aria-label={`Image ${i + 1}`}
                          style={{ borderColor: i === imgIdx ? "#111827" : undefined }}
                        >
                          <img src={src} alt="" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Infos produit */}
                <div className="prod-info">
                  <h1 className="prod-title">{m.lib}</h1>
                  {m.repXpert?.designation && m.repXpert.designation !== m.lib && (
                    <p className="prod-subDesig">{m.repXpert.designation}</p>
                  )}

                  <div className="prod-topMeta">
                    <div className="prod-topMeta__left">
                      <div className="kv">
                        <span className="k">Réf. article :</span>
                        <span className="v">{m.cod || "—"}</span>
                      </div>
                      {m.refArt && m.refArt !== m.cod && (
                        <div className="kv">
                          <span className="k">Réf. d'origine :</span>
                          <span className="v">{m.refArt}</span>
                        </div>
                      )}
                      {m.codArtOrig && m.codArtOrig !== m.cod && m.codArtOrig !== m.refArt && (
                        <div className="kv">
                          <span className="k">Code art. orig. :</span>
                          <span className="v">{m.codArtOrig}</span>
                        </div>
                      )}
                      <div className="kv">
                        <span className="k">Marque :</span>
                        <span className="v">{m.brandLabel || "—"}</span>
                      </div>
                      {m.repXpert?.artNumber && (
                        <div className="kv">
                          <span className="k">Réf. fournisseur :</span>
                          <span className="v">{m.repXpert.artNumber}</span>
                        </div>
                      )}
                      {m.repXpert?.ean && (
                        <div className="kv">
                          <span className="k">EAN :</span>
                          <span className="v" style={{ fontFamily: "monospace", letterSpacing: ".04em" }}>{m.repXpert.ean}</span>
                        </div>
                      )}
                      {m.origine && (
                        <div className="kv">
                          <span className="k">Origine :</span>
                          <span className="v" style={{ textTransform: "capitalize" }}>{m.origine}</span>
                        </div>
                      )}
                    </div>

                    <div className="prod-topMeta__right">
                      <div className={`avail ${m.inStock ? "ok" : "ko"}`}>
                        <i className="dot" />
                        {m.inStock ? `Disponible ` : "Hors stock"}
                      </div>
                      
                      
                      {m.remise != null && m.remise > 0 && (
                        <div className="kv">
                          <span className="k">Remise :</span>
                          <span className="v" style={{ color: "#16a34a", fontWeight: 600 }}>-{m.remise} %</span>
                        </div>
                      )}
                      {m.repXpert?.vehicules?.length > 0 && (
                        <div className="kv" style={{ alignItems: "flex-start" }}>
                          <span className="k">Véhicules :</span>
                          <span className="v" style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {m.repXpert.vehicules.map((v, i) => (
                              <span key={i} className="rp-tag">{v}</span>
                            ))}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="prod-priceRow">
                    {m.inStock
                      ? <div className="priceNow" style={{ color: priceColor }}>
                          {m.priceTTC != null && !Number.isNaN(m.priceTTC) ? `${fmt(m.priceTTC)} DT` : "—"}
                        </div>
                      : <div className="priceNow muted">Prix indisponible</div>
                    }
                  </div>

                  <div className="prod-actions">
                    {showAddButton ? (
                      <button
                        className="btnPrimary"
                        style={{ background: brandColor, display: "inline-flex", alignItems: "center", gap: 8 }}
                        onClick={onAddToCart}
                        disabled={!m.inStock}
                        title={m.inStock ? "Ajouter au panier" : "Hors stock"}
                      >
                        <FiShoppingCart className="cartIcon" aria-hidden />
                        <span>AJOUTER AU PANIER</span>
                      </button>
                    ) : (
                      <div className="qtyBox">
                        <button type="button" className="qbtn" onClick={onDec} aria-label="Diminuer"><FiMinus /></button>
                        <input className="qinput" type="number" min={1} step={1} value={qty} onChange={onChangeQtyInput} />
                        <button type="button" className="qbtn" onClick={onInc} aria-label="Augmenter"><FiPlus /></button>
                      </div>
                    )}
                  </div>

                  <div className="prod-bottomLinks">
                    <ProductWishlistButton
                      article={{
                        codArt: m.cod,
                        name: m.lib,
                        price: m.priceTTC,
                        image: m.image,
                        link: `/produits/${encodeURIComponent(m.cod || "NA")}`,
                        rawItem: sourceItem,
                      }}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* ── Spécifications + Références OEM (tags) ── */}
            <section className="prod-card">
              <div className="prod-sectionGrid">
                <div>
                  <h3 className="prod-secTitle">Spécifications</h3>
                  {m.specs?.length ? (
                    <div className="specs-grid">
                      {m.specs.map((s, i) => (
                        <div className="spec-item" key={i}>
                          <div className="spec-name">{s.name}</div>
                          <div className="spec-value">{s.value || "—"}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="muted">Aucune spécification.</div>
                  )}
                </div>

                {m.repXpert?.oem?.length > 0 && (
                  <div>
                    <h3 className="prod-secTitle">Références OEM</h3>
                    <div className="oem-tags">
                      {m.repXpert.oem.map((r, i) => (
                        <span key={i} className="rp-tag">{r}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* ── Détail OEM par constructeur ── */}
            {m.repXpert?.oemDetail?.length > 0 && (
              <section className="prod-card">
                <h3 className="prod-secTitle">Références constructeurs</h3>
                <div className="oemdetail-wrap">
                  {groupByManufacturer(m.repXpert.oemDetail).map(([mfr, entries]) => (
                    <div key={mfr} className="oemdetail-group">
                      <div className="oemdetail-mfr">{mfr}</div>
                      <div className="oemdetail-list">
                        {entries.map((e, i) => (
                          <div key={i} className="oemdetail-row">
                            <span className="rp-tag">{e.number}</span>
                            {e.exchangeability && (
                              <span className="oemdetail-exch">{e.exchangeability}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ── Produits équivalents ── */}
            {m.repXpert?.produits?.length > 0 && (
              <section className="prod-card">
                <h3 className="prod-secTitle">Produits équivalents</h3>
                <div className="rp-produits-grid">
                  {m.repXpert.produits.map((p, i) => (
                    <div key={i} className="rp-produit-card">
                      <div className="rp-produit-img">
                        {p.imageUrl
                          ? <img src={p.imageUrl} alt={p.designation} loading="lazy" />
                          : <div className="rp-produit-imgPh" />
                        }
                      </div>
                      <div className="rp-produit-body">
                        <div className="rp-produit-brand">{p.brand}</div>
                        <div className="rp-produit-ref">{p.ref}</div>
                        <div className="rp-produit-desig">{p.designation}</div>
                        <div className={`rp-produit-statut${p.statut === "Normal" ? " ok" : " ko"}`}>
                          {p.statut}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ── Compatibilités véhicules (TecDoc) ── */}
            {m.cars?.length > 0 && (
              <section className="prod-card">
                <h3 className="prod-secTitle">Compatibilités véhicules</h3>
                <div className="cars-list">
                  <div className="cars-head">
                    <div>Constructeur</div>
                    <div>Modèle</div>
                    <div>Motorisation</div>
                    <div>Période</div>
                  </div>
                  {m.cars.map((c, i) => (
                    <div className="cars-row" key={i}>
                      <div>{c.make}</div>
                      <div>{c.model}</div>
                      <div>{c.engine || "—"}</div>
                      <div>{(c.start || "").slice(0, 10)} {"→"} {(c.end || "").slice(0, 10)}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </main>
  );
}
